/**
 * Sitewide FOMO bar.
 *
 * One prominent strip carrying the headline promise, a live countdown and the
 * only next step. Copy lives in `@/config/pricing` so the same sentence follows
 * the learner from the landing page to checkout.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { analytics } from "@/lib/analytics";
import { useMembership } from "@/hooks/useMembership";

import {
  OFFER_COUPON_CODE,
  OFFER_ENDS,
  OFFER_HEADLINE,
  OFFER_IS_FLASH,
  DUAL_LIST_PRICE_LABEL,
  DUAL_OFFER_PRICE_LABEL,
} from "@/config/pricing";

function useCountdown(target: Date) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ms = target.getTime() - now;
  if (ms <= 0) return null;
  const s = Math.floor(ms / 1000);
  return {
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
  };
}

export default function FomoOfferBar({ className }: { className?: string }) {
  const { loading, isActive } = useMembership();
  const left = useCountdown(OFFER_ENDS);
  const seen = useRef(false);

  const visible = OFFER_IS_FLASH && !loading && !isActive && !!left;

  useEffect(() => {
    if (!visible || seen.current) return;
    seen.current = true;
    analytics.offerImpression({ surface: "offer_bar", coupon: OFFER_COUPON_CODE });
  }, [visible]);

  if (!visible || !left) return null;

  const pad = (n: number) => String(n).padStart(2, "0");

  return (
    <div
      className={cn(
        "w-full border-b border-primary/40 bg-primary text-primary-foreground",
        className,
      )}
      role="region"
      aria-label="Limited time offer"
      data-testid="fomo-offer-bar"
    >
      <div className="container mx-auto flex min-h-[56px] flex-col items-center justify-center gap-1.5 px-4 py-2.5 text-center sm:flex-row sm:gap-4 sm:py-2">
        <Link
          to="/payment"
          onClick={() =>
            analytics.offerClick({
              surface: "offer_bar",
              coupon: OFFER_COUPON_CODE,
              destination: "/payment",
            })
          }
          className={cn(
            "flex flex-col items-center gap-1.5 transition-opacity hover:opacity-95 sm:flex-row sm:gap-4",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-primary",
          )}
        >
          <span className="flex items-center gap-2 text-sm font-extrabold leading-snug sm:text-base">
            <Sparkles className="h-4 w-4 shrink-0 animate-pulse" aria-hidden="true" />
            {OFFER_HEADLINE}
            <span className="whitespace-nowrap underline underline-offset-4">
              Register now
            </span>
          </span>

          <span className="flex flex-wrap items-center justify-center gap-2 text-xs font-bold sm:text-sm">
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-background/15 px-2.5 py-1 tabular-nums"
              data-testid="offer-countdown"
            >
              <Clock className="h-3.5 w-3.5" aria-hidden="true" />
              <span aria-live="polite">
                {left.days}d {pad(left.hours)}h {pad(left.minutes)}m {pad(left.seconds)}s
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-background/15 px-2.5 py-1">
              <span className="line-through opacity-70">{DUAL_LIST_PRICE_LABEL}</span>
              <span>{DUAL_OFFER_PRICE_LABEL}</span>
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-background px-2.5 py-1 text-primary">
              Code {OFFER_COUPON_CODE}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
          </span>
        </Link>
      </div>
    </div>
  );
}

